import { useEffect, useRef, useState } from "react";

/**
 * Fires `visible=true` once its target scrolls into view. Reset by calling
 * `reset()` (useful for testing / dev). Uses IntersectionObserver — no lib.
 */
export function useInView(options = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!ref.current || typeof window === "undefined") return;
    const el = ref.current;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            // one-shot — stop observing once seen
            observer.unobserve(el);
          }
        }
      },
      { threshold: 0.2, rootMargin: "0px 0px -8% 0px", ...options }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, visible];
}

/**
 * Count-up hook — animates from 0 → `to` over `duration` ms once `active` flips
 * true. Returns the current integer value.
 */
export function useCountUp(to, { duration = 1200, active = true, delay = 0 } = {}) {
  const [value, setValue] = useState(0);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!active || startedRef.current) return;
    startedRef.current = true;
    const target = Number(to) || 0;
    if (target === 0) {
      setValue(0);
      return;
    }
    let raf;
    let timeoutId = setTimeout(() => {
      const start = performance.now();
      const step = (now) => {
        const elapsed = now - start;
        const t = Math.min(1, elapsed / duration);
        // ease-out cubic
        const eased = 1 - Math.pow(1 - t, 3);
        setValue(Math.round(target * eased));
        if (t < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay);
    return () => {
      clearTimeout(timeoutId);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [active, to, duration, delay]);

  return value;
}
